import React from "react";
import { darkenHex } from "../../../services/utility/BrandColorExtractor";
import "./styles/BusinessMonogramAvatar.css";

interface BusinessMonogramAvatarProps {
  name: string;
  /** Hex accent — typically from useBusinessBrandColor. */
  accent: string;
  size?: number;
}

/** Initials from a name — first letter of the first two words, or the first
 *  two letters of a single-word name. Always 1-2 chars, so the avatar circle
 *  never overflows regardless of name length. Exported so other avatar-style
 *  chips (BusinessOwnerPill) share this exact convention instead of keeping
 *  their own copy — deliberately NOT the same as the app's other
 *  `InitialsHelper.getInitials` (services/helpers), which returns one letter
 *  per word and "" for an empty name; that helper already has its own
 *  callers (search results, onboarding), so this stays a separate, smaller
 *  convention rather than risking a visible change there. */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Personalized fallback for a business's logo slot — a colored monogram
 * (initials on a gradient of the business's own brand accent) instead of a
 * generic platform-purple Store icon. Every business looks distinct even
 * before it has a logo uploaded.
 */
const BusinessMonogramAvatar: React.FC<BusinessMonogramAvatarProps> = ({
  name,
  accent,
  size = 26,
}) => {
  const initials = getInitials(name);
  return (
    <div
      className="biz-monogram-avatar"
      style={{
        background: `linear-gradient(135deg, ${accent} 0%, ${darkenHex(accent)} 100%)`,
        fontSize: `${size * 0.42}px`,
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

export default BusinessMonogramAvatar;
